import React, { useState } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './RegisterStation.css';

const STATES = [
  'Andhra Pradesh','Telangana','Karnataka','Tamil Nadu','Maharashtra',
  'Gujarat','Rajasthan','Uttar Pradesh','Madhya Pradesh','West Bengal',
  'Kerala','Punjab','Haryana','Bihar','Odisha','Other',
];

const EMPTY = {
  name: '', address: '', gstin: '', state: '', phone: '',
};

/**
 * Register New Station (parent–sub model).
 *
 * The logged-in user submits a request to add another fuel station under
 * their existing account. The new station is created with status='Pending'
 * and is_active=0 — invisible until the Noris team approves it.
 *
 * Notes:
 *   • No new login is created. Same client_id, new fuel_stations row.
 *   • Once approved, the station appears in the station-selector dropdown
 *     in the top header and can be used like any other station.
 *   • Existing data is never affected — each station has its own data scope.
 */
export default function RegisterStation() {
  const { user } = useAuth();
  const [form, setForm]       = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(null);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.name.trim()) return 'Station name is required.';
    if (form.gstin && !/^[0-9A-Z]{15}$/.test(form.gstin.trim().toUpperCase())) {
      return 'GSTIN must be 15 alphanumeric characters.';
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true); setError(''); setSuccess(null);
    try {
      const data = await api.stations.register({
        name:    form.name.trim(),
        address: form.address.trim(),
        gstin:   form.gstin.trim().toUpperCase(),
        state:   form.state.trim(),
        phone:   form.phone.trim(),
      });
      setSuccess(data);
      setForm(EMPTY);
    } catch (err) {
      setError(err.message || 'Could not submit station registration.');
    }
    setLoading(false);
  };

  const reset = () => { setForm(EMPTY); setError(''); setSuccess(null); };

  /* ─── Success view ─── */
  if (success) {
    return (
      <div className="rs-page">
        <div className="page-header">
          <h2 className="page-title">Register New Station</h2>
        </div>

        <div className="rs-success-card">
          <div className="rs-success-icon">⏳</div>
          <h3 className="rs-success-title">Your station is pending approval</h3>
          <p className="rs-success-msg">
            <strong>{success.name}</strong> has been submitted to the Noris
            platform team for review. You will see the station in your top-bar
            station selector with a <em>Pending</em> badge until it is approved.
          </p>

          <div className="rs-status-pills">
            <div className="rs-pill rs-pill-active">
              <span className="rs-pill-step">1</span>
              <span>Submitted</span>
            </div>
            <div className="rs-pill-arrow">→</div>
            <div className="rs-pill rs-pill-active">
              <span className="rs-pill-step">2</span>
              <span>Pending review</span>
            </div>
            <div className="rs-pill-arrow">→</div>
            <div className="rs-pill">
              <span className="rs-pill-step">3</span>
              <span>Approved</span>
            </div>
          </div>

          <div className="rs-info-box">
            ℹ <strong>What happens next?</strong> The Noris team will verify
            your station details and approve or reject the request. Once
            approved, the station becomes selectable in the top bar. You will
            continue to use your existing login (<code>{user?.email}</code>) —
            no new credentials are created.
          </div>

          <div className="rs-success-actions">
            <button className="btn btn-primary" onClick={reset}>＋ Register Another Station</button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Form view ─── */
  return (
    <div className="rs-page">
      <div className="page-header">
        <h2 className="page-title">Register New Station</h2>
      </div>

      <div className="rs-intro">
        Submit a request to add another fuel station to your account. After
        the Noris team approves your request, the new station will appear in
        the station selector in the top bar and you can switch to it any time.
        The new station has its own independent data — sales, meter readings,
        and reports stay separated by station.
      </div>

      {error && <div className="rs-error">⚠️ {error}</div>}

      <form onSubmit={submit} autoComplete="off" className="rs-form">
        <div className="section-card">
          <div className="section-header">Station Details</div>
          <div className="rs-grid">

            <div className="form-group rs-col-2">
              <label>Station Name <span className="req">*</span></label>
              <input type="text" placeholder="e.g. Sri Lakshmi Filling Station — Highway 65"
                     value={form.name} onChange={f('name')} required />
            </div>

            <div className="form-group">
              <label>GSTIN</label>
              <input type="text" placeholder="36XXXXX1234X1ZX"
                     value={form.gstin}
                     onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                     maxLength={15} />
              <div className="form-hint">15 alphanumeric chars (optional)</div>
            </div>

            <div className="form-group">
              <label>State</label>
              <select value={form.state} onChange={f('state')}>
                <option value="">-- Select --</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input type="tel" placeholder="10-digit station contact"
                     value={form.phone} onChange={f('phone')} maxLength={15} />
            </div>

            <div className="form-group rs-col-2">
              <label>Station Address</label>
              <input type="text" placeholder="NH-65, Village, District, State"
                     value={form.address} onChange={f('address')} />
            </div>
          </div>
        </div>

        <div className="rs-info-box">
          ℹ <strong>This station will be added under your existing account
          ({user?.email}).</strong> No new login is created. After Noris team
          approval, the station appears in the top-bar selector.
        </div>

        <div className="rs-actions">
          <button type="button" className="btn btn-light" onClick={reset} disabled={loading}>Reset</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? '⏳ Submitting…' : '🚀 Submit for Approval'}
          </button>
        </div>
      </form>
    </div>
  );
}
